// Implements: F-UIX-018, F-USG-008
/**
 * The distribution of one organization (UIX.6), as the server answered it to its reader.
 *
 * - **The org admin's whole view**: the clamp warning when the server flags that the saved
 *   distribution exceeds the ceiling (N3), the ceiling and what a person with no limit of their
 *   own gets, one row per person (the organization's people, then any saved limit of somebody
 *   who left) and one row per team (the organization's teams, then any allocation of a team it no
 *   longer lists), with what is allocated out of the organization budget.
 * - **A team lead's view**: the teams it leads and their allocation, read-only.
 *
 * The edit controls exist only when the server says `can_manage` (the permission answer, never
 * a role name); without it the same rows show their values as text. Rows are keyed by person and
 * team id (checklist 14). Money is formatted here only, inside the backoffice (OD-12).
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { moneyText } from "../../../shared/ui/moneyText";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import type {
  MemberView,
  OrganizationPolicyView,
  PersonLimitInput,
  TeamPolicyView,
  WriteOutcome,
} from "../../../types/usagePolicy";
import { allocatedMicros } from "../model/distribution";
import { PersonLimitRow, TeamAllocationRow } from "./DistributionRows";
import styles from "./Distribution.module.css";
import usage from "./MyUsage.module.css";

const USD = "USD";

export interface DistributionWriteActions {
  readonly setPersonLimit: (personId: string, input: PersonLimitInput) => Promise<WriteOutcome>;
  readonly clearPersonLimit: (personId: string) => Promise<WriteOutcome>;
  readonly setTeamAllocation: (teamId: string, monthlyBudgetMicros: number) => Promise<WriteOutcome>;
  readonly clearTeamAllocation: (teamId: string) => Promise<WriteOutcome>;
}

export interface OrganizationDistributionProps {
  readonly policy: OrganizationPolicyView;
  readonly people: readonly MemberView[];
  /** The organization's teams by id, as its list names them. */
  readonly teamNames: ReadonlyMap<string, string>;
  readonly actions: DistributionWriteActions;
}

function CeilingSummary({ policy }: { readonly policy: OrganizationPolicyView }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const { ceiling } = policy;
  const bound = (value: string | null): string => value ?? t("backoffice.distribution.ceiling.unbounded");

  return (
    <section className={usage.section} aria-labelledby={headingId}>
      <h3 id={headingId} className={usage.cardTitle}>
        {t("backoffice.distribution.ceiling.title")}
      </h3>
      {ceiling === null ? (
        <p className={usage.hint}>{t("backoffice.distribution.ceiling.none")}</p>
      ) : (
        <dl className={usage.figures}>
          <dt>{t("backoffice.distribution.ceiling.generations")}</dt>
          <dd>{bound(ceiling.max_generations_per_person?.toString() ?? null)}</dd>
          <dt>{t("backoffice.distribution.ceiling.budget")}</dt>
          <dd>{bound(ceiling.max_budget_micros_per_person === null ? null : moneyText(ceiling.max_budget_micros_per_person, USD))}</dd>
          <dt>{t("backoffice.distribution.ceiling.organization")}</dt>
          <dd>{bound(ceiling.organization_budget_micros === null ? null : moneyText(ceiling.organization_budget_micros, USD))}</dd>
        </dl>
      )}
      <p className={usage.hint}>
        {t("backoffice.distribution.defaults", {
          generations: policy.default_generations,
          budget: moneyText(policy.default_budget_micros, USD),
        })}
      </p>
    </section>
  );
}

export function OrganizationDistribution({ policy, people, teamNames, actions }: OrganizationDistributionProps): ReactElement {
  const { t } = useTranslation();
  const peopleId = useId();
  const teamsId = useId();
  const canManage = policy.can_manage;
  const saved = new Map(policy.people.map((row) => [row.person_id, row] as const));
  const members = new Set(people.map((member) => member.user.id));
  const formerLimits = policy.people.filter((row) => !members.has(row.person_id));
  const allocations = new Map(policy.teams.map((row) => [row.team_id, row.monthly_budget_micros] as const));
  const unlistedTeams = policy.teams.filter((row) => !teamNames.has(row.team_id));

  const personRow = (personId: string, name: string | null, email: string | null): ReactElement => (
    <PersonLimitRow
      key={personId}
      name={name}
      email={email}
      saved={saved.get(personId)}
      policy={policy}
      canManage={canManage}
      onSave={(input) => actions.setPersonLimit(personId, input)}
      onClear={() => actions.clearPersonLimit(personId)}
    />
  );
  const teamRow = (teamId: string, name: string | null): ReactElement => (
    <TeamAllocationRow
      key={teamId}
      name={name}
      teamId={teamId}
      saved={allocations.get(teamId) ?? null}
      policy={policy}
      canManage={canManage}
      onSave={(amount) => actions.setTeamAllocation(teamId, amount)}
      onClear={() => actions.clearTeamAllocation(teamId)}
    />
  );

  return (
    <>
      {/* Mounted empty and filled when flagged, so a screen reader announces the warning. */}
      <div role="status" aria-live="polite">
        {policy.distribution_exceeds_ceiling ? (
          <p className={styles.warning}>{t("backoffice.distribution.warning.exceeds")}</p>
        ) : null}
      </div>
      {canManage ? null : <p className={usage.hint}>{t("backoffice.distribution.readOnly")}</p>}
      <CeilingSummary policy={policy} />

      <section className={usage.section} aria-labelledby={peopleId}>
        <h3 id={peopleId} className={usage.cardTitle}>
          {t("backoffice.distribution.people.title")}
        </h3>
        {people.length === 0 && formerLimits.length === 0 ? (
          <EmptyState title={t("backoffice.distribution.people.title")} body={t("backoffice.distribution.people.empty")} />
        ) : (
          <>
            {canManage ? <p className={usage.hint}>{t("backoffice.distribution.people.hint")}</p> : null}
            <table className={usage.table} aria-labelledby={peopleId}>
              <thead>
                <tr>
                  <th scope="col">{t("backoffice.distribution.column.person")}</th>
                  <th scope="col">{t("backoffice.distribution.column.generations")}</th>
                  <th scope="col">{t("backoffice.distribution.column.budget")}</th>
                  <th scope="col">{t("backoffice.distribution.column.applies")}</th>
                  {canManage ? <th scope="col">{t("backoffice.distribution.column.actions")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {people.map((member) => personRow(member.user.id, member.user.full_name, member.user.email))}
                {formerLimits.map((row) => personRow(row.person_id, null, null))}
              </tbody>
            </table>
          </>
        )}
      </section>

      <section className={usage.section} aria-labelledby={teamsId}>
        <h3 id={teamsId} className={usage.cardTitle}>
          {t("backoffice.distribution.teams.title")}
        </h3>
        <p className={usage.hint}>
          {t("backoffice.distribution.teams.allocated", {
            allocated: moneyText(allocatedMicros(policy), USD),
            budget: moneyText(policy.organization_budget_micros, USD),
          })}
        </p>
        {teamNames.size === 0 && unlistedTeams.length === 0 ? (
          <EmptyState title={t("backoffice.distribution.teams.title")} body={t("backoffice.distribution.teams.empty")} />
        ) : (
          <table className={usage.table} aria-labelledby={teamsId}>
            <thead>
              <tr>
                <th scope="col">{t("backoffice.distribution.column.team")}</th>
                <th scope="col">{t("backoffice.distribution.column.allocation")}</th>
                {canManage ? <th scope="col">{t("backoffice.distribution.column.actions")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {[...teamNames].map(([teamId, name]) => teamRow(teamId, name))}
              {unlistedTeams.map((row) => teamRow(row.team_id, null))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export interface TeamDistributionProps {
  readonly policy: TeamPolicyView;
  /** The led teams' names, as the session carries them. */
  readonly teamNames: ReadonlyMap<string, string>;
}

/** A team lead's view: each led team's allocation, read-only. No control is rendered. */
export function TeamDistribution({ policy, teamNames }: TeamDistributionProps): ReactElement {
  const { t } = useTranslation();
  const teamsId = useId();

  return (
    <>
      <p className={usage.hint}>{t("backoffice.distribution.readOnly")}</p>
      <section className={usage.section} aria-labelledby={teamsId}>
        <h3 id={teamsId} className={usage.cardTitle}>
          {t("backoffice.distribution.teams.title")}
        </h3>
        <table className={usage.table} aria-labelledby={teamsId}>
          <thead>
            <tr>
              <th scope="col">{t("backoffice.distribution.column.team")}</th>
              <th scope="col">{t("backoffice.distribution.column.allocation")}</th>
            </tr>
          </thead>
          <tbody>
            {policy.teams.map((row) => (
              <tr key={row.team_id}>
                <th scope="row">{teamNames.get(row.team_id) ?? t("backoffice.distribution.team.unknown")}</th>
                <td>
                  {row.monthly_budget_micros === null
                    ? t("backoffice.distribution.teams.none")
                    : moneyText(row.monthly_budget_micros, USD)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
